
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { api } from '@/utils/api-client';

export interface LicenseInfo {
  license_key: string;
  product_code?: string;
  expiration_date: string;
  last_checked: string;
  is_valid: boolean;
  features?: string[];
  error_message?: string;
}

export interface LicenseStatus {
  isValid: boolean;
  isExpired: boolean;
  isWithinGracePeriod: boolean;
  daysUntilExpiration: number;
  daysSinceExpiration: number;
  gracePeriodDays: number;
  isConnectionFailure?: boolean;
}

interface LicenseContextType {
  licenseInfo: LicenseInfo | null;
  licenseStatus: LicenseStatus | null;
  isLoading: boolean;
  error: string | null;
  refetchLicense: () => void;
  checkLicenseStatus: () => LicenseStatus | null;
}

const LicenseContext = createContext<LicenseContextType>({
  licenseInfo: null,
  licenseStatus: null,
  isLoading: true,
  error: null,
  refetchLicense: () => { },
  checkLicenseStatus: () => null,
});

interface LicenseProviderProps {
  children: ReactNode;
}

export const LicenseProvider = ({ children }: LicenseProviderProps) => {
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Grace period configuration (3 days)
  // Grace period configuration (3 days)
  const GRACE_PERIOD_DAYS = 3;

  // Mock license data instead of fetching - Memoize to prevent infinite loop
  const licenseData: LicenseInfo = React.useMemo(() => ({
    license_key: 'UNLIMITED-LICENSE',
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    last_checked: new Date().toISOString(),
    is_valid: true,
    features: ['all'],
  }), []);

  const queryLoading = false;
  const queryError = null;
  const refetch = useCallback(() => { }, []);

  /*
  // Use tRPC query for license data
  const {
    data: licenseData,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = api.system.getLicense.useQuery(undefined, {
    refetchInterval: 60000, // Refetch every minute
    refetchOnWindowFocus: true,
    retry: 3,
  });
  */

  // Calculate license status based on license info
  const calculateLicenseStatus = useCallback(
    (license: LicenseInfo | null): LicenseStatus | null => {
      if (!license) return null;

      // Check for connection failure
      if (license.license_key === 'CONNECTION_FAILED') {
        return {
          isValid: false,
          isExpired: true,
          isWithinGracePeriod: false,
          daysUntilExpiration: 0,
          daysSinceExpiration: 0,
          gracePeriodDays: GRACE_PERIOD_DAYS,
          isConnectionFailure: true,
        };
      }

      const now = new Date();
      const expirationDate = new Date(license.expiration_date);

      // Calculate days until/since expiration
      const timeDiff = expirationDate.getTime() - now.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

      const isExpired = daysDiff < 0;
      const daysUntilExpiration = Math.max(0, daysDiff);
      const daysSinceExpiration = Math.max(0, -daysDiff);
      const isWithinGracePeriod = isExpired && daysSinceExpiration <= GRACE_PERIOD_DAYS;

      // License is considered "valid" if not expired OR within grace period
      const isValid = !isExpired || isWithinGracePeriod;

      return {
        isValid,
        isExpired,
        isWithinGracePeriod,
        daysUntilExpiration,
        daysSinceExpiration,
        gracePeriodDays: GRACE_PERIOD_DAYS,
        isConnectionFailure: false,
      };
    },
    [GRACE_PERIOD_DAYS],
  );

  // Update license info and status when data changes
  useEffect(() => {
    setIsLoading(queryLoading);

    if (queryError) {
      // Set a connection failure license info for proper handling
      const connectionFailureLicense = {
        license_key: 'CONNECTION_FAILED',
        expiration_date: new Date().toISOString(),
        last_checked: new Date().toISOString(),
        is_valid: false,
        error_message: 'Unable to connect to license server',
      };

      setError(queryError.message || 'Failed to fetch license information');
      setLicenseInfo(connectionFailureLicense);
      const status = calculateLicenseStatus(connectionFailureLicense);
      setLicenseStatus(status);
    } else if (licenseData) {
      setError(null);
      setLicenseInfo(licenseData);
      const status = calculateLicenseStatus(licenseData);
      setLicenseStatus(status);
    }
  }, [licenseData, queryLoading, queryError, calculateLicenseStatus]);

  const refetchLicense = useCallback(() => {
    refetch();
  }, [refetch]);

  const checkLicenseStatus = useCallback(() => {
    return licenseStatus;
  }, [licenseStatus]);

  const value: LicenseContextType = {
    licenseInfo,
    licenseStatus,
    isLoading,
    error,
    refetchLicense,
    checkLicenseStatus,
  };

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
};

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

export default LicenseProvider;
